import { UseFormRegisterReturn, useForm } from "react-hook-form";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Business, BusinessRequest } from "../../../service/model/business.model";
import { Box, Button, Dialog, DialogTitle, Grid } from "@mui/material";
import FormField from "../../../components/form-controls/form-field";
import theme from "../../../app/app-theme";
import usePlans from "../../../hooks/use-plans";
import FormCloseButton from "../../../components/form-controls/form-close-button";
import { FormTextField } from "../../../components/form-input-field-wrappers/form-controller-inputs/form-text-field";
import { FormSelectField } from "../../../components/form-input-field-wrappers/form-controller-inputs/form-select-field";

interface BusinessFormProps {
    readonly business?: Business
    readonly editable: boolean
    readonly onClose: () => void
    readonly onSubmit: (data: BusinessRequest) => void
}

const style = {
    // position: 'absolute' as 'absolute',
    // top: '50%',
    // left: '50%',
    // transform: 'translate(-50%, -50%)',
    // width: '100%',
    // bgcolor: 'background.paper',
    // border: '2px solid #000',
    // boxShadow: 24,
    p: 2,
};

const validationSchema = yup.object().shape({
    name: yup.string().required("Name is required"),
    address: yup.string(),
    email: yup.string().email("Not a valid email"),
    contactPerson: yup.string(),
    contactNumber: yup.string(),
    planId: yup.number().transform((value) => Number.isNaN(value) ? null : value).required("Plan is required"),
    discountPercentage: yup.number().min(0).max(100),
    dueAmount: yup.number(),
    nextBillingDate: yup.string(),
    createdDate: yup.string(),
    status: yup.string().required("Status is required")
});

export type FormValues = yup.InferType<typeof validationSchema>;

const BusinessForm: React.FC<BusinessFormProps> = ({ business, editable, onClose, onSubmit }) => {
    const [plans] = usePlans();

    const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
        resolver: yupResolver(validationSchema)
    });

    const onSubmitHandler = (data: any) => {
        const updatedBusiness = {
            ...business,
            ...data,
            planId: data.planId,
        } as BusinessRequest;
        onSubmit(updatedBusiness)
    };

    const getRegister = (name: keyof FormValues, validation?: any): UseFormRegisterReturn => {
        return register(name, validation);
    };

    return (
        <Dialog open={true} onClose={onClose} sx={style} fullWidth maxWidth='md'>
            <DialogTitle sx={{ paddingBottom: 0 }}>Business Details</DialogTitle>

            <Box sx={style}>
                <>
                    <FormCloseButton onClose={onClose} />
                    <form onSubmit={handleSubmit(onSubmitHandler)}>
                        <Grid container direction="row" className="mt-0 my-0" justifyContent='space-between'>
                            <FormField>
                                <FormTextField
                                    id="name"
                                    label="Name"
                                    name="name"
                                    defaultValue={business?.name}
                                    errorMessage={errors.name?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormSelectField
                                    id={"plan"}
                                    label="Plan"
                                    name="planId"
                                    defaultValue={business?.plan.id}
                                    errorMessage={errors.planId?.message}
                                    options={plans.map(plan => { return { label: plan.name, value: plan.id } })}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="address"
                                    label="Address"
                                    name="address"
                                    defaultValue={business?.address}
                                    errorMessage={errors.address?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="contact"
                                    label="Contact Number"
                                    name="contactNumber"
                                    defaultValue={business?.contactNumber}
                                    errorMessage={errors.contactNumber?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="person"
                                    label="Contact Person"
                                    name="contactPerson"
                                    defaultValue={business?.contactPerson}
                                    errorMessage={errors.contactPerson?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="email"
                                    label="Email"
                                    name="email"
                                    defaultValue={business?.email}
                                    errorMessage={errors.email?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="discountPercentage"
                                    label="Discount Percentage"
                                    type="number"
                                    name="discountPercentage"
                                    defaultValue={business?.discountPercentage ?? 0}
                                    errorMessage={errors.discountPercentage?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="dueAmount"
                                    label="Due Amount"
                                    name="dueAmount"
                                    type="number"
                                    defaultValue={business?.dueAmount ?? 0}
                                    errorMessage={errors.dueAmount?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="nextBillingDate"
                                    label="Next Billing Date"
                                    type="date"
                                    name="nextBillingDate"
                                    defaultValue={business?.nextBillingDate ?? ''}
                                    errorMessage={errors.nextBillingDate?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="createdDate"
                                    label="Joined Date"
                                    type="date"
                                    name="createdDate"
                                    defaultValue={business?.createdDate ?? new Date().toISOString().slice(0, 10)}
                                    errorMessage={errors.createdDate?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormSelectField
                                    id={"status"}
                                    label="Status"
                                    name="status"
                                    defaultValue={business?.status}
                                    errorMessage={errors.status?.message}
                                    options={["Trial", "Active", "Expired", "Deleted", "Suspended"].map(item => { return { label: item, value: item } })}
                                    register={register}
                                />
                            </FormField>
                        </Grid>
                        <Grid container direction="row" justifyContent='end' sx={{ mt: 1 }}>
                            <Button type="button" variant="contained" sx={{ mr: 1, bgcolor: `${theme.palette.secondary.main}` }} onClick={onClose}>Cancel</Button>
                            <Button type="submit" variant="contained">Submit</Button>
                        </Grid>
                    </form>
                </>
            </Box>
        </Dialog>
    );
}

export default BusinessForm;